import { pageLoadComplete } from "@lululemon/mwa-perf";
import url from "url";
import { Router } from "@lululemon/ecom-core";
import { getSatelliteScript } from "@lululemon/mwa-ab-testing/esm/globalScriptProviders/satellite";
import { publish as lapelPublish } from "@lululemon/mwa-ab-testing/esm/utils/pubsub";

import {
  BEGIN_FETCHING_PAGE_DATA,
  COMPLETE_FETCHING_PAGE_DATA,
  COMPLETE_CACHED_PAGE_LOAD,
  ANALYTICS_DATA_LAYER_LOADED,
} from "../actions/page-data-actions";
import { clearError } from "../../lib/analytics";
import Util from "../../lib/util";

const util = new Util("./store/middleware/page-load-tracker.js");
const $window = util.windowEvent();

const PAGE_LOAD_BEGIN = "page-load-begin";
const PAGE_UPDATE = "page-update";

export function pageLoadTrackerMiddleware() {
  const state = {
    pageLoadComplete: false,
    digitalData: undefined,
    prevUrl: Router.asPath,
  };
  const flags = {
    authReady: { value: false, timeout: undefined },
    personalization: { value: false, timeout: undefined },
    documentReady: { value: false, timeout: undefined },
    digitalData: { value: false, timeout: undefined },
    pageData: { value: false, timeout: undefined },
    vendorsLoaded: { value: false, timeout: undefined },
  };

  const updateDigitalData = () => {
    if (!(state.digitalData && typeof state.digitalData === "object")) {
      return;
    }
    const { user = {}, cart } = state.digitalData;
    const [digitalDataUser] = $window.digitalData?.user || [{}];
    Object.assign($window.digitalData, {
      user: [{ ...digitalDataUser, ...user, sessionId: "" }],
      cart,
    });
  };

  // Fire smetrics if you are last one to set a flag to true (edge case, all flags are already true, exit early if all flags are true before applying update)
  const updateFlag = (name, value) => {
    if (!flags || !flags[name]) {
      return;
    }
    flags[name].value = value;
    flags[name].timeout = clearTimeout(flags[name].timeout);
    if (
      flags.authReady.value &&
      flags.documentReady.value &&
      flags.vendorsLoaded.value &&
      flags.digitalData.value &&
      flags.pageData.value &&
      flags.personalization.value &&
      !state.pageLoadComplete
    ) {
      state.pageLoadComplete = true;
      try {
        updateDigitalData();
        requestIdleCallback(
          () =>
            getSatelliteScript()
              .then(async (_satellite) => {
                await checkUpdateCookies();
                pageLoadComplete(_satellite);
              })
              .catch(() => {
                // Do Nothing
              }),
          { timeout: 1400 }
        );
      } catch (err) {
        // Do Nothing
      }
    }
  };

  const timeoutFlag = (name, ms) => {
    clearTimeout(flags[name].timeout);
    flags[name].timeout = setTimeout(updateFlag, ms, name, true);
  };

  timeoutFlag("documentReady", 10000);
  timeoutFlag("vendorsLoaded", 10000);
  timeoutFlag("digitalData", 10000);
  timeoutFlag("authReady", 10000);

  const trackVirtualPageTurn = async () => {
    try {
      state.pageLoadComplete = false;
      updateFlag("personalization", false);
      timeoutFlag("personalization", 5000);
      const _satellite = await getSatelliteScript();
      _satellite.track(PAGE_LOAD_BEGIN);
    } catch (err) {
      // Do Nothing
    }
  };
  // Use redux loading as the first virtual page turn
  trackVirtualPageTurn();

  // Clear any existing error states when route change begins
  Router.events.on("routeChangeStart", () => {
    clearError();
  });

  // Re-initialize trackers on NextJS route change complete if URL has changed
  Router.events.on("routeChangeComplete", (url) => {
    if (shouldTrack(state.prevUrl, url)) {
      trackVirtualPageTurn();
    }
    state.prevUrl = url;
  });

  // Listener fires once, only when readystatechange runs

  const docReady = () => {
    updateFlag("documentReady", true);
    requestIdleCallback(
      () => {
        // Initiates Launch pipeline to trigger vendor callbacks
        getSatelliteScript()
          .then((_satellite) => {
            _satellite.track("window-onload");
          })
          .catch((error) => {
            // Do Nothing
          });
      },
      { timeout: 1400 }
    );
  };

  const windowLoaded =
    performance
      ?.getEntriesByType?.("navigation")
      ?.every?.((e) => e?.loadEventEnd) || document.readyState === "complete";
  if (windowLoaded) {
    docReady();
  } else {
    const readyListener = () => {
      $window.removeEventListener("load", readyListener);
      docReady();
    };
    $window.addEventListener("load", readyListener);
  }

  // Create listener for lazy-loaded Personalization & ABTesting
  document.addEventListener("LAPEL_BATCH_REQUEST_COMPLETE", () => {
    updateFlag("personalization", true);
  });

  if ($window.launchVendorsLoaded) {
    updateFlag("vendorsLoaded", true);
  } else {
    $window.addEventListener(
      "launch-vendors-loaded",
      function _listener() {
        $window.removeEventListener("launch-vendors-loaded", _listener, true);
        $window.launchVendorsLoaded = true;
        updateFlag("vendorsLoaded", true);
      },
      true
    );
  }

  // Listen for Okta Sessions which are refreshing to prevent missing authenticated visits
  if ($window.oktaSessionRefreshComplete) {
    updateFlag("authReady", true);
  } else {
    $window.addEventListener(
      "okta-session-refresh-complete",
      function _okta_listener() {
        $window.removeEventListener(
          "okta-session-refresh-complete",
          _okta_listener,
          true
        );
        $window.oktaSessionRefreshComplete = true;
        updateFlag("authReady", true);
      },
      true
    );
  }

  // Counter for page-data fetches
  let activeFetches = 0;
  return ({ getState }) =>
    (next) =>
    (action) => {
      // Increment counter
      if (action.type === BEGIN_FETCHING_PAGE_DATA) {
        if (activeFetches === 0) {
          lapelPublish("pageDataFetchBegin");
          updateFlag("pageData", false);
        }
        activeFetches += 1;
      }

      // Decrement counter
      if (action.type === COMPLETE_FETCHING_PAGE_DATA) {
        activeFetches = Math.max(activeFetches - 1, 0);
        // This part makes no sense @FrancisLi
        if (action.disableAnalyticsPageLoad) {
          activeFetches ||
            getSatelliteScript()
              .then(() => {
                _satellite.track(PAGE_UPDATE);
              })
              .catch(() => {
                // Do Nothing
              });
          updateFlag("pageData", false);
        } else if (activeFetches === 0) {
          // When counter reaches 0 mark this req as resolved...
          // Possible race condition
          updateFlag("pageData", true);
        }
        if (activeFetches === 0) {
          lapelPublish("pageDataFetchComplete", { cached: false });
        }
      }

      // If page data is cached rather than fetched instantly resolve...
      if (action.type === COMPLETE_CACHED_PAGE_LOAD) {
        updateFlag("pageData", true);
        lapelPublish("pageDataFetchComplete", { cached: true });
      }

      // Data that gets set to window object for Analytics?
      if (action.type === ANALYTICS_DATA_LAYER_LOADED) {
        state.digitalData = action.digitalData;
        updateFlag("digitalData", true);
      }
      return next(action);
    };
}

function checkUpdateCookies() {
  return new Promise(async (resolve, reject) => {
    /*
     To save ourselves unnecessary calls, lets make sure that we are using Safari.
     Prior to utilizing the cookie update window.
    */
    const { userAgent } = $window.navigator;
    const isChrome = userAgent.includes("Chrome");
    const isSafari = userAgent.includes("Safari");
    if (!isSafari || (isSafari && isChrome)) return resolve();
    if (hasValidCookie("s_ecid")) return resolve();
    try {
      //TODO: Implement Promise.all when we need to change more than one cookie.
      await updateCookie("s_ecid");
      resolve();
    } catch (e) {
      console.warn(e);
      resolve();
    }
  });
}

function updateCookie(cookieName = "") {
  return new Promise((resolve, reject) => {
    const url = `/api/personalization/cookies/upgrade?name=${cookieName}`;

    fetch(url, {
      method: "GET",
      credentials: "include",
    })
      .then(() => {
        cacheCookies(cookieName);
        return resolve();
      })
      .catch((err) => reject(err));
  });
}

function cacheCookies(cookieName) {
  const key = `cached-cookie-time-${cookieName}`;
  try {
    $window.localStorage.setItem(key, new Date().toString());
  } catch (err) {
    console.warn(err);
  }
}

const oneDay = 1000 * 60 * 60 * 25;

function hasValidCookie(cookieName) {
  const key = `cached-cookie-time-${cookieName}`;
  try {
    let cookieExpiration = $window.localStorage.getItem(key);
    cookieExpiration = new Date(cookieExpiration);
    if (!cookieExpiration) return false;
    const currentDate = new Date();
    const diff = currentDate.getTime() - cookieExpiration.getTime();
    const diffDays = Math.round(diff / oneDay);
    return diffDays < 365; //Refresh the Cookie Once a Year
  } catch (err) {
    console.warn(err);
    return false;
  }
}

// do not instance trackers when url changes for simple things like
// color or size selection when those cause updates in the URL
// Effectivly ignores querystring changes on pdp pages
function shouldTrack(oldUrl, newUrl) {
  if (!oldUrl || !newUrl) return true;

  const parsedOld = url.parse(oldUrl);
  const parsedNew = url.parse(newUrl);
  return !(
    isPdp(parsedOld.pathname) &&
    isPdp(parsedNew.pathname) &&
    parsedOld.pathname === parsedNew.pathname
  );
}

function isPdp(pathname = "") {
  return pathname.indexOf("/p/") !== -1;
}
